import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EventClassResponse, UserSearchResult, HorseResponse} from '../../../../../../../../../../../../@shared/models/response.module';
import {EventsService} from '../../../../../../../../../../../../@shared/services/events-service';
import {ToastService} from '../../../../../../../../../../../../@core/services/toast.service';
import {EndpointService} from '../../../../../../../../../../../../@shared/endpoints.service';
import {Horses} from '../../../../../../../../../../../../@shared/services/horses';
import {AddHorse} from '../../../../../../../../../../../../pages/user-profile/components/user-profile-horses/components/add-horse/add-horse';
import {debounceTime, distinctUntilChanged, switchMap, Subject} from 'rxjs';
import {AQH_HOR_0} from '../../../../../../../../../../../../@shared/models/input.module';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-add-class-registration',
  standalone: false,
  templateUrl: './add-class-registration.html',
  styleUrl: './add-class-registration.css'
})
export class AddClassRegistration implements OnInit {
  @Input() eventClass!: EventClassResponse;

  // Member type selection
  memberType: 'existing' | 'manual' = 'existing';

  // User search
  userSearchQuery = '';
  userSearchResults: UserSearchResult[] = [];
  selectedUser: UserSearchResult | null = null;
  isSearchingUsers = false;
  private searchSubject = new Subject<string>();

  // Manual user entry
  firstName = '';
  lastName = '';
  email = '';
  phone = '';

  // Horse selection
  availableHorses: HorseResponse[] = [];
  selectedHorse: HorseResponse | null = null;
  loadingHorses = false;

  // Payment options
  paymentType: 'manual' | 'send_link' = 'manual';
  paymentStatus: 'not_required' | 'pay_later' | 'paid' = 'paid';
  paymentMethod: 'cash' | 'bank_transfer' | 'other' = 'cash';

  // Submission
  submitting = false;
  submitted = false;
  errorMessage = '';

  // Validation helpers
  get isUserInvalid(): boolean {
    if (this.memberType === 'existing') {
      return this.submitted && !this.selectedUser;
    }
    return this.submitted && (!this.firstName.trim() || !this.lastName.trim());
  }

  get isFirstNameInvalid(): boolean {
    return this.submitted && this.memberType === 'manual' && !this.firstName.trim();
  }

  get isLastNameInvalid(): boolean {
    return this.submitted && this.memberType === 'manual' && !this.lastName.trim();
  }

  get isHorseInvalid(): boolean {
    return this.submitted && !this.selectedHorse;
  }

  constructor(
    public activeModal: NgbActiveModal,
    private eventsService: EventsService,
    private endpointService: EndpointService,
    private horsesService: Horses,
    private toastService: ToastService,
    private modalService: NgbModal,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    // Setup debounced user search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (query.length < 2) {
          return [];
        }
        this.isSearchingUsers = true;
        return this.endpointService.aqh_eve_67({ search: query });
      })
    ).subscribe({
      next: (response: any) => {
        this.isSearchingUsers = false;
        this.userSearchResults = response?.data || [];
      },
      error: () => {
        this.isSearchingUsers = false;
        this.userSearchResults = [];
      }
    });

    // Load all horses
    this.loadAllHorses();
  }

  loadAllHorses() {
    this.loadingHorses = true;
    const payload: AQH_HOR_0 = {};

    this.horsesService.getHorses(payload).subscribe({
      next: (horses: HorseResponse[]) => {
        this.availableHorses = horses || [];
        this.loadingHorses = false;
      },
      error: () => {
        this.loadingHorses = false;
        this.availableHorses = [];
      }
    });
  }

  onMemberTypeChange() {
    // Reset fields when switching types
    this.selectedUser = null;
    this.userSearchQuery = '';
    this.userSearchResults = [];
    this.firstName = '';
    this.lastName = '';
    this.email = '';
    this.phone = '';
    this.errorMessage = '';
  }

  onUserSearch() {
    this.searchSubject.next(this.userSearchQuery);
  }

  selectUser(user: UserSearchResult) {
    this.selectedUser = user;
    this.userSearchQuery = user.full_name || `${user.first_name} ${user.last_name}`;
    this.userSearchResults = [];
  }

  clearSelectedUser() {
    this.selectedUser = null;
    this.userSearchQuery = '';
  }

  onHorseChanged(horseId: any) {
    this.selectedHorse = this.availableHorses.find(h => h.horse_id === horseId) || null;
  }

  openAddHorseModal() {
    const modalRef = this.modalService.open(AddHorse, { size: 'lg', centered: true });

    modalRef.result.then((newHorse: HorseResponse) => {
      if (newHorse) {
        this.toastService.success('Horse added successfully');
        this.loadAllHorses();
      }
    }, () => {});
  }

  close() {
    this.activeModal.dismiss();
  }

  isValid(): boolean {
    // Must have user
    if (this.memberType === 'existing') {
      if (!this.selectedUser) return false;
    } else {
      if (!this.firstName.trim() || !this.lastName.trim()) return false;
    }

    // Must have horse for class registration
    if (!this.selectedHorse) return false;

    return true;
  }

  submit() {
    this.submitted = true;

    if (!this.isValid()) {
      this.toastService.error(this.translateService.instant('manageEvent.addClassRegistration.validationError'));
      return;
    }

    // For send_link, we need email
    if (this.paymentType === 'send_link') {
      const userEmail = this.memberType === 'existing' ? this.selectedUser?.email : this.email;
      if (!userEmail) {
        this.errorMessage = this.translateService.instant('manageEvent.addClassRegistration.emailRequired');
        return;
      }
    }

    this.submitting = true;
    this.errorMessage = '';

    const payload: any = {
      event_class_id: this.eventClass.event_class_id,
      horse_id: this.selectedHorse?.horse_id,
      // Payment options
      payment_type: this.paymentType,
      // Use 'pay_later' for send_link (confirmed but awaiting payment)
      // 'pending' is reserved for users mid-checkout who haven't paid
      payment_status: this.paymentType === 'manual' ? this.paymentStatus : 'pay_later',
      payment_method: this.paymentType === 'manual' ? this.paymentMethod : undefined,
      send_payment_link: this.paymentType === 'send_link'
    };

    if (this.memberType === 'existing' && this.selectedUser) {
      payload.user_id = this.selectedUser.user_id;
    } else {
      // Manual entry - include user details for backend to create/find user
      payload.first_name = this.firstName.trim();
      payload.last_name = this.lastName.trim();
      payload.email = this.email.trim() || undefined;
      payload.phone = this.phone.trim() || undefined;
    }

    this.eventsService.createClassRegistration(payload).subscribe({
      next: () => {
        if (this.paymentType === 'send_link') {
          this.toastService.success(this.translateService.instant('manageEvent.addClassRegistration.successWithLink'));
        } else {
          this.toastService.success(this.translateService.instant('manageEvent.addClassRegistration.success'));
        }
        this.activeModal.close(true);
      },
      error: (err) => {
        this.submitting = false;
        this.errorMessage = err.error?.reason || this.translateService.instant('manageEvent.addClassRegistration.error');
      }
    });
  }

  formatPrice(price: number, currency: string): string {
    return `${price.toFixed(2)} ${currency}`;
  }
}
